import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { documentosAPI, fabricantesAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import PDFViewerPro from '../../components/PDFViewerPro';
import PrecacheButtonWithBadge from '../../components/PrecacheButtonWithBadge';
import { IndividualPrecacheStatusProvider } from '../../contexts/IndividualPrecacheStatusContext';
import R2FileUpload from '../../components/R2FileUpload';
import { 
  Plus, Search, FileText, AlertTriangle, BookOpen, 
  Loader2, Eye, Upload, Edit, DollarSign, X, Filter, Newspaper, Trash2, CheckCircle, BellOff
} from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

// Helper para convertir fechas de varios formatos a YYYY-MM-DD (formato HTML date input)
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  
  // Si ya está en formato ISO o YYYY-MM-DD, extraer solo la fecha
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  
  // Si es formato YYYY-MM-DD, devolverlo tal cual
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Intentar parsear otros formatos (DD-Mon-YYYY, DD-MM-YYYY, etc.)
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    console.warn('Error parsing date:', dateStr);
  }
  
  return '';
};

const AdminDocumentosV2 = () => {
  const [searchParams] = useSearchParams();
  const [manuales, setManuales] = useState([]);
  const [directivas, setDirectivas] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('buscar') || '');

  // Sincroniza el filtro de búsqueda con ?buscar= (deep-link desde la campanita).
  useEffect(() => {
    const q = searchParams.get('buscar');
    if (q !== null) setSearchTerm(q);
  }, [searchParams]);
  const [showForm, setShowForm] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showResubirConfirm, setShowResubirConfirm] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFab, setSelectedFab] = useState('');
  const [revisionCambiada, setRevisionCambiada] = useState(false);
  const [revisionOriginal, setRevisionOriginal] = useState('');
  const [fechaRevisionOriginal, setFechaRevisionOriginal] = useState('');
  const [revisionPdfFile, setRevisionPdfFile] = useState(null);
  // Checkbox: precachear el documento al terminar la subida (LOW -> HD -> OCR)
  // POR DEFECTO DESTILDADO: el admin decide explícitamente si quiere renderizar al momento
  // de subir (paga tiempo ahora, todo listo cuando el cliente abre) o dejar para más tarde
  // desde el card del listado (o desde el visor). Ver flujo completo en handoff PRD 2026-08-09.
  const [precachearAlSubir, setPrecachearAlSubir] = useState(false);
  // Estado para el diálogo de confirmación de PDFs > 150MB
  const [showLargePrecacheConfirm, setShowLargePrecacheConfirm] = useState(false);
  const [largePrecacheContext, setLargePrecacheContext] = useState(null); // {manualId, docName, sizeMB, resolveFn}
  
  // Filtros globales
  const [filterFabricante, setFilterFabricante] = useState('');
  const [filterModelo, setFilterModelo] = useState('');
  const [filterModelos, setFilterModelos] = useState([]);
  
  // Para agregar modelos de diferentes fabricantes
  const [modeloSelectorFab, setModeloSelectorFab] = useState('');
  const [modeloSelectorModelos, setModeloSelectorModelos] = useState([]);
  const [modelosSeleccionados, setModelosSeleccionados] = useState([]);
  
  const [manualForm, setManualForm] = useState({
    fabricante_id: '',
    modelos_aplicables: [],
    tipo_manual: 'manual_mantenimiento',
    tipo_manual_custom: '',
    numero_referencia: '',
    nombre: '',
    revision: '',
    fecha_revision: '',
    precio_usd: 0,
    es_documento_general: false,
    anular_notificaciones: false
  });

  // TIPOS DE DOCUMENTOS - Lista completa
  const tiposDocumento = [
    // Manuales
    { value: 'manual_mantenimiento', label: 'Manual de Mantenimiento', categoria: 'Manuales' },
    { value: 'manual_servicio', label: 'Manual de Servicio', categoria: 'Manuales' },
    { value: 'catalogo_partes', label: 'Catálogo Ilustrado de Partes', categoria: 'Manuales' },
    { value: 'manual_practicas_estandar', label: 'Manual de Prácticas Estándar', categoria: 'Manuales' },
    { value: 'overhaul_manual', label: 'Overhaul Manual', categoria: 'Manuales' },
    { value: 'publicacion_tecnica', label: 'Publicación Técnica', categoria: 'Manuales' },
    { value: 'inspection_report', label: 'Inspection Report', categoria: 'Manuales' },
    { value: 'progressive_inspection', label: 'Progressive Inspection', categoria: 'Manuales' },
    { value: 'wiring_diagram', label: 'Wiring Diagram', categoria: 'Manuales' },
    { value: 'programmed_inspection', label: 'Programmed Inspection', categoria: 'Manuales' },
    { value: 'airworthiness_program', label: 'Airworthiness Program', categoria: 'Manuales' },
    { value: 'application_guide', label: 'Application Guide', categoria: 'Manuales' },
    { value: 'tools_catalog', label: 'Tools Catalog', categoria: 'Manuales' },
    { value: 'manual_operaciones', label: 'Manual de Operaciones', categoria: 'Manuales' },
    { value: 'manual_propietario', label: 'Manual de Propietario', categoria: 'Manuales' },
    { value: 'manual_reparacion', label: 'Manual de Reparación Estructural', categoria: 'Manuales' },
    { value: 'shop_manual', label: 'Shop Manual', categoria: 'Manuales' },
    { value: 'special_inspection', label: 'Special Inspection', categoria: 'Manuales' },
    { value: 'trouble_shooting', label: 'Trouble Shooting', categoria: 'Manuales' },
    { value: 'manual_vuelo', label: 'Manual de Vuelo', categoria: 'Manuales' },
    // Publicaciones de Servicio
    { value: 'directiva_ad', label: 'Directiva de Aeronavegabilidad', categoria: 'Publicaciones de Servicio' },
    { value: 'service_bulletin', label: 'Service Bulletin', categoria: 'Publicaciones de Servicio' },
    // Otras Publicaciones
    { value: 'service_letter', label: 'Service Letter', categoria: 'Otras Publicaciones' },
    { value: 'service_instruction', label: 'Service Instruction', categoria: 'Otras Publicaciones' },
    { value: 'service_advisory', label: 'Service Advisory', categoria: 'Otras Publicaciones' },
    { value: 'circular_tecnica', label: 'Circular Técnica', categoria: 'Otras Publicaciones' },
    { value: 'comunicaciones', label: 'Comunicaciones', categoria: 'Otras Publicaciones' },
  ];

  // Tipos por categoría
  const tiposManuales = tiposDocumento.filter(t => t.categoria === 'Manuales').map(t => t.value);
  const tiposPublicacionesServicio = tiposDocumento.filter(t => t.categoria === 'Publicaciones de Servicio').map(t => t.value);
  const tiposOtrasPublicaciones = tiposDocumento.filter(t => t.categoria === 'Otras Publicaciones').map(t => t.value);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedFab) {
      loadModelos(selectedFab);
    }
  }, [selectedFab]);

  // Cargar modelos para filtro global
  useEffect(() => {
    if (filterFabricante) {
      loadFilterModelos(filterFabricante);
    } else {
      setFilterModelos([]);
      setFilterModelo('');
    }
  }, [filterFabricante]);

  const loadData = async () => {
    try {
      // Primer lote rapido + directivas + fabricantes en paralelo
      const [manualesRes, directivasRes, fabRes] = await Promise.all([
        documentosAPI.getManuales({ limit: 50, page: 1 }),
        documentosAPI.getDirectivas({ limit: 500 }),
        fabricantesAPI.getAll({ limit: 500 })
      ]);
      const manualesData = manualesRes.data?.data || manualesRes.data || [];
      setManuales(Array.isArray(manualesData) ? manualesData : []);
      setDirectivas(directivasRes.data?.data || directivasRes.data || []);
      setFabricantes(fabRes.data?.data || fabRes.data || []);
      setLoading(false);

      // Cargar el resto de manuales en background
      const totalPages = manualesRes.data?.pagination?.pages || 1;
      if (totalPages > 1) {
        const allManuales = [...(Array.isArray(manualesData) ? manualesData : [])];
        for (let page = 2; page <= totalPages; page++) {
          try {
            const nextRes = await documentosAPI.getManuales({ limit: 50, page });
            const nextData = nextRes.data?.data || [];
            if (Array.isArray(nextData) && nextData.length > 0) {
              allManuales.push(...nextData);
              setManuales([...allManuales]);
            }
          } catch {
            break;
          }
        }
      }
    } catch (error) {
      toast.error('Error al cargar documentos');
      setLoading(false);
    }
  };

  const loadModelos = async (fabId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabId);
      setModelos(response.data);
    } catch (error) {
      console.error('Error loading modelos:', error);
    }
  };

  const loadFilterModelos = async (fabId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabId);
      setFilterModelos(response.data);
    } catch (error) {
      setFilterModelos([]);
    }
  };

  const loadModelosSelector = async (fabId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabId);
      setModeloSelectorModelos(response.data);
    } catch (error) {
      setModeloSelectorModelos([]);
    }
  };

  const agregarModelo = (modelo) => {
    const fabricante = fabricantes.find(f => f.id === modeloSelectorFab);
    if (!modelosSeleccionados.find(m => m.id === modelo.id)) {
      setModelosSeleccionados([...modelosSeleccionados, {
        id: modelo.id,
        nombre: modelo.nombre,
        fabricante_nombre: fabricante?.nombre || '',
        fabricante_id: modeloSelectorFab
      }]);
      // Usar modelo.nombre para consistencia con cómo se guardan los documentos
      setManualForm(prev => ({
        ...prev,
        modelos_aplicables: [...prev.modelos_aplicables, modelo.nombre]
      }));
    }
  };

  const quitarModelo = (modeloId) => {
    const modeloAQuitar = modelosSeleccionados.find(m => m.id === modeloId);
    setModelosSeleccionados(modelosSeleccionados.filter(m => m.id !== modeloId));
    setManualForm(prev => ({
      ...prev,
      // Filtrar por nombre ya que así se guardan
      modelos_aplicables: prev.modelos_aplicables.filter(nombre => nombre !== modeloAQuitar?.nombre)
    }));
  };

  // Seleccionar todos los modelos de un fabricante
  const seleccionarTodosModelos = (checked) => {
    if (checked && modeloSelectorModelos.length > 0) {
      const fabricante = fabricantes.find(f => f.id === modeloSelectorFab);
      const nuevosModelos = modeloSelectorModelos
        .filter(m => !modelosSeleccionados.find(sel => sel.id === m.id))
        .map(m => ({
          id: m.id,
          nombre: m.nombre,
          fabricante_nombre: fabricante?.nombre || '',
          fabricante_id: modeloSelectorFab
        }));
      
      setModelosSeleccionados([...modelosSeleccionados, ...nuevosModelos]);
      setManualForm(prev => ({
        ...prev,
        modelos_aplicables: [...prev.modelos_aplicables, ...nuevosModelos.map(m => m.nombre)]
      }));
    }
  };

  const handleViewPdf = (doc) => {
    if (!doc.pdf_url && !doc.pdf_path) {
      toast.error('Este documento no tiene un PDF asociado');
      return;
    }
    setSelectedDoc(doc);
    setShowPdfViewer(true);
  };

  const handleEditManual = async (manual) => {
    const tiposPredefinidos = tiposDocumento.map(t => t.value);
    const esPersonalizado = !tiposPredefinidos.includes(manual.tipo_manual);
    
    setRevisionOriginal(manual.revision || '');
    setFechaRevisionOriginal(manual.fecha_revision ? manual.fecha_revision.split('T')[0] : '');
    setRevisionCambiada(false);
    setRevisionPdfFile(null);
    
    setManualForm({
      fabricante_id: manual.fabricante_id || '',
      modelos_aplicables: manual.modelos_aplicables || [],
      tipo_manual: esPersonalizado ? 'otro' : (manual.tipo_manual || 'manual_mantenimiento'),
      tipo_manual_custom: esPersonalizado ? manual.tipo_manual : '',
      numero_referencia: manual.numero_referencia || '',
      nombre: manual.nombre || '',
      revision: manual.revision || '',
      fecha_revision: formatDateForInput(manual.fecha_revision),
      precio_usd: manual.precio_usd || 0,
      es_documento_general: manual.es_documento_general || false,
      anular_notificaciones: false
    });
    setSelectedDoc(manual);
    
    // Cargar modelos existentes - OPTIMIZADO para cargar en paralelo
    if (manual.modelos_aplicables?.length > 0) {
      try {
        // Primero, cargar TODOS los modelos de TODOS los fabricantes en paralelo
        const promesas = fabricantes.map(async (fab) => {
          try {
            const res = await fabricantesAPI.getModelos(fab.id);
            return { fabricante: fab, modelos: res.data || [] };
          } catch (e) {
            return { fabricante: fab, modelos: [] };
          }
        });
        
        const resultados = await Promise.all(promesas);
        
        // Indexar modelos por fabricante_id -> nombre -> modelo
        // Esto permite resolver correctamente cuando hay modelos con el mismo nombre en diferentes fabricantes
        const modelosPorFabricante = {};
        const fabricantesPorId = {};
        
        for (const { fabricante, modelos } of resultados) {
          fabricantesPorId[fabricante.id] = fabricante;
          modelosPorFabricante[fabricante.id] = {};
          for (const modelo of modelos) {
            modelosPorFabricante[fabricante.id][modelo.nombre] = {
              ...modelo,
              fabricante_id: fabricante.id,
              fabricante_nombre: fabricante.nombre
            };
          }
        }
        
        // Ahora resolver cada modelo del documento
        // Manejar AMBOS formatos:
        // - Formato NUEVO: "fabricante_id_modelo_nombre" (ej: "17142244-1336-45e2-9317-74b97cd2e554_PA-12")
        // - Formato ANTIGUO: solo nombre (ej: "PA-12")
        const modelosInfo = [];
        const docFabricanteId = manual.fabricante_id;
        
        for (const modeloRef of manual.modelos_aplicables) {
          let modeloEncontrado = null;
          let modeloNombre = modeloRef;
          let modeloFabricanteId = null;
          
          // Detectar si es formato nuevo (contiene UUID pattern seguido de _)
          // UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx_
          const uuidPattern = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_(.+)$/i;
          const match = modeloRef.match(uuidPattern);
          
          if (match) {
            // Formato nuevo: extraer fabricante_id y nombre
            modeloFabricanteId = match[1];
            modeloNombre = match[2];
            
            // Buscar directamente en el fabricante especificado
            if (modelosPorFabricante[modeloFabricanteId]?.[modeloNombre]) {
              modeloEncontrado = modelosPorFabricante[modeloFabricanteId][modeloNombre];
            }
          } else {
            // Formato antiguo: solo nombre
            // PRIMERO: Buscar en el fabricante del documento
            if (docFabricanteId && modelosPorFabricante[docFabricanteId]?.[modeloRef]) {
              modeloEncontrado = modelosPorFabricante[docFabricanteId][modeloRef];
            }
            
            // SEGUNDO: Si no se encuentra, buscar en todos los fabricantes
            if (!modeloEncontrado) {
              for (const [fabId, modelos] of Object.entries(modelosPorFabricante)) {
                if (modelos[modeloRef]) {
                  modeloEncontrado = modelos[modeloRef];
                  break;
                }
              }
            }
          }
          
          if (modeloEncontrado) {
            modelosInfo.push({
              id: modeloEncontrado.id,
              nombre: modeloEncontrado.nombre,
              fabricante_id: modeloEncontrado.fabricante_id,
              fabricante_nombre: modeloEncontrado.fabricante_nombre
            });
          } else {
            // FALLBACK: Crear entrada con la info disponible
            const fabId = modeloFabricanteId || docFabricanteId || '';
            modelosInfo.push({
              id: fabId ? `${fabId}_${modeloNombre}` : modeloNombre,
              nombre: modeloNombre,
              fabricante_id: fabId,
              fabricante_nombre: fabricantesPorId[fabId]?.nombre || '(Desconocido)'
            });
          }
        }
        
        setModelosSeleccionados(modelosInfo);
        console.log(`Cargados ${modelosInfo.length} modelos para documento ${manual.nombre}`);
      } catch (e) {
        console.error('Error loading modelos info:', e);
        // FALLBACK: Crear entradas básicas con el fabricante del documento
        const fallbackModelos = manual.modelos_aplicables.map(m => ({
          id: `${manual.fabricante_id}_${m}`,
          nombre: m,
          fabricante_id: manual.fabricante_id || '',
          fabricante_nombre: '(Error al cargar)'
        }));
        setModelosSeleccionados(fallbackModelos);
      }
    } else {
      setModelosSeleccionados([]);
    }
    
    if (manual.fabricante_id) {
      setSelectedFab(manual.fabricante_id);
    }
    setShowForm(true);
  };

  // Pregunta al admin si quiere confirmar el precacheo de un PDF grande (>150MB).
  // Devuelve {willPrecache: bool, forceLarge: bool} para que quien llama pueda decidir
  // si difiere o no las notificaciones a usuarios ANTES de subir el PDF.
  const askPrecacheDecision = async (fileSizeBytes) => {
    const sizeMB = fileSizeBytes ? fileSizeBytes / (1024 * 1024) : 0;
    const LARGE_THRESHOLD_MB = 150;

    if (sizeMB <= LARGE_THRESHOLD_MB) {
      return { willPrecache: true, forceLarge: false };
    }

    const confirmed = await new Promise((resolve) => {
      setLargePrecacheContext({
        manualId: null,
        docName: '',
        sizeMB: Math.round(sizeMB),
        resolveFn: resolve,
      });
      setShowLargePrecacheConfirm(true);
    });

    if (!confirmed) {
      toast.info('Precacheo saltado. Podés ejecutarlo luego desde el panel de Estado de Documentación.');
      return { willPrecache: false, forceLarge: false };
    }
    return { willPrecache: true, forceLarge: true };
  };

  // Encola el precacheo individual (LOW -> HD -> OCR) para un documento
  // recien subido, dada la decisión ya tomada por el admin.
  const enqueuePrecacheIndividual = async (manualId, docName, forceLarge, silentOnError = false) => {
    try {
      const res = await documentosAPI.precachearIndividual(manualId, forceLarge);
      const data = res.data || {};
      if (data.pending_after_batch) {
        toast.info(`Precacheo encolado. Se procesará al terminar el batch masivo en curso. Te avisamos por la campanita.`);
      } else {
        toast.success(`Precacheo encolado para "${docName}". Te avisamos por la campanita cuando termine.`);
      }
      return true;
    } catch (err) {
      console.warn('[Precache Individual] Error:', err);
      if (!silentOnError) {
        toast.warning('No se pudo encolar el precacheo automático. Podés ejecutarlo manualmente desde el panel.');
      }
      return false;
    }
  };



  const handleSaveManual = async () => {
    if (!manualForm.numero_referencia || !manualForm.nombre || !manualForm.fabricante_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }    
    const esEdicion = !!selectedDoc?.id;
    const cambioRevision = esEdicion && manualForm.revision !== revisionOriginal && revisionOriginal !== '';
    
    // REGLA DE NEGOCIO: Si cambió la revisión, DEBE cambiar fecha_revision Y subir nuevo PDF
    if (cambioRevision) {
      const fechaCambio = manualForm.fecha_revision !== fechaRevisionOriginal;
      if (!fechaCambio) {
        toast.error('Al cambiar la revisión, debe actualizar también la fecha de revisión');
        return;
      }
      if (!revisionPdfFile) {
        toast.error('Al cambiar la revisión, debe seleccionar el nuevo PDF');
        return;
      }
      
      const confirmar = window.confirm(
        `Está cambiando la revisión de "${revisionOriginal}" a "${manualForm.revision}".\n\n` +
        `Se subirá el nuevo PDF y se notificará a los usuarios cuando esté listo.\n\n` +
        `¿Desea continuar?`
      );
      if (!confirmar) return;
    }
    
    // REGLA DE NEGOCIO: Documento nuevo debe tener PDF
    if (!esEdicion && !revisionPdfFile) {
      toast.error('Debe seleccionar un PDF para crear el documento');
      return;
    }
    
    setSaving(true);
    try {
      const tipoFinal = manualForm.tipo_manual === 'otro' && manualForm.tipo_manual_custom
        ? manualForm.tipo_manual_custom.toLowerCase().replace(/\s+/g, '_')
        : manualForm.tipo_manual;

      // IMPORTANTE: Guardar el ID completo del modelo (fabricante_id_nombre) para evitar conflictos
      // cuando diferentes fabricantes tienen modelos con el mismo nombre (ej: PA-12 en Piper y FS 2003)
      // El ID tiene formato: {fabricante_id}_{modelo_nombre}
      const modelosAGuardar = modelosSeleccionados.length > 0 
        ? modelosSeleccionados.map(m => {
            // Asegurar que usamos el ID completo, no solo el nombre
            // Si el modelo ya tiene fabricante_id, construir el ID
            if (m.fabricante_id && m.nombre) {
              return `${m.fabricante_id}_${m.nombre}`;
            }
            // Fallback: si ya es un ID completo (contiene UUID pattern), usarlo
            if (m.id && m.id.includes('-') && m.id.includes('_')) {
              return m.id;
            }
            // Último recurso: usar el fabricante del documento actual
            if (manualForm.fabricante_id && m.nombre) {
              return `${manualForm.fabricante_id}_${m.nombre}`;
            }
            return m.nombre || m.id || m;
          })
        : (manualForm.modelos_aplicables || []);
      
      console.log('Guardando documento con modelos:', modelosAGuardar);
      console.log('Fabricante ID del formulario:', manualForm.fabricante_id);

      // IMPORTANTE: Preservar la fecha tal como la ingresó el usuario (sin conversión UTC)
      // El input type="date" devuelve YYYY-MM-DD, lo guardamos con hora 12:00 UTC
      // para evitar que el cambio de zona horaria cambie el día
      let fechaRevision = new Date().toISOString();
      if (manualForm.fecha_revision) {
        const [year, month, day] = manualForm.fecha_revision.split('-').map(Number);
        fechaRevision = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
      }

      const data = {
        ...manualForm,
        tipo_manual: tipoFinal,
        modelos_aplicables: modelosAGuardar,
        fecha_revision: fechaRevision
      };
      
      // Limpiar campo auxiliar de display antes de enviar
      delete data.precio_usd_display;
      delete data.tipo_manual_custom;
      
      console.log('Data a guardar:', JSON.stringify(data, null, 2));
      
      // Helper: sube PDF con fallback a proxy si CORS falla (preview/dev)
      const uploadPdfToR2 = async (docId, pdfFile) => {
        try {
          // Intento 1: Subida directa a R2 (rápido, funciona en producción)
          const fd = new FormData();
          fd.append('filename', pdfFile.name);
          fd.append('content_type', 'application/pdf');
          const presignedRes = await documentosAPI.getR2PresignedUrl(docId, fd);
          const { upload_url, file_key } = presignedRes.data;
          
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve();
              else reject(new Error('DIRECT_UPLOAD_FAILED'));
            };
            xhr.onerror = () => reject(new Error('CORS_ERROR'));
            xhr.open('PUT', upload_url);
            xhr.setRequestHeader('Content-Type', 'application/pdf');
            xhr.send(pdfFile);
          });
          return file_key;
        } catch (directErr) {
          // Intento 2: Proxy vía backend (evita CORS)
          console.warn('[Upload] Subida directa falló, usando proxy:', directErr.message);
          toast.info('Usando subida alternativa...');
          const proxyRes = await documentosAPI.proxyUploadPdf(docId, pdfFile);
          return proxyRes.data.file_key;
        }
      };

      if (selectedDoc?.id) {
        // === EDICIÓN DE DOCUMENTO EXISTENTE ===
        
        if (cambioRevision && revisionPdfFile) {
          // FLUJO ATÓMICO: Primero decidir precacheo, luego subir PDF, luego actualizar metadata
          // Si el PDF falla, la metadata NO se actualiza → todo queda como estaba
          
          // Paso 0: decidir el precacheo AHORA (antes de subir) para saber si diferir notificaciones
          let willPrecache = false;
          let forceLarge = false;
          if (precachearAlSubir) {
            const decision = await askPrecacheDecision(revisionPdfFile.size);
            willPrecache = decision.willPrecache;
            forceLarge = decision.forceLarge;
          }
          
          toast.info('Subiendo nuevo PDF...');
          
          try {
            // Helper: sube PDF con fallback automático a proxy si CORS falla
            const file_key = await uploadPdfToR2(selectedDoc.id, revisionPdfFile);
            
            // Confirmar subida (limpia cache viejo, guarda r2_file_key).
            // No hay notif en este paso para revisiones (es_documento_nuevo=false),
            // pero seteamos defer=false porque las notifs viajan por updateManual.
            const confirmFormData = new FormData();
            confirmFormData.append('file_key', file_key);
            await documentosAPI.confirmR2Upload(selectedDoc.id, confirmFormData, false);

          } catch (uploadErr) {
            console.error('Error subiendo PDF:', uploadErr);
            toast.error('Error al subir el PDF. Los datos del documento NO fueron modificados.');
            setSaving(false);
            return; // NO guardar metadata si el PDF falló
          }
          
          // Paso 4: PDF confirmado → actualizar metadata ANTES de encolar el render.
          // Con defer_notification=willPrecache el aviso queda pendiente y ligado al
          // r2_file_key nuevo antes de que el worker pueda terminar (PDFs de pocas páginas).
          await documentosAPI.updateManual(selectedDoc.id, data, true, willPrecache);

          // Paso 5: encolar el precacheo. Si falla, el aviso sigue pendiente (no se envía)
          // y se dispara cuando un render posterior de esta misma versión termine OK.
          if (willPrecache) {
            const enqueued = await enqueuePrecacheIndividual(
              selectedDoc.id,
              manualForm.numero_referencia || manualForm.nombre || 'documento',
              forceLarge,
              true
            );
            if (enqueued) {
              toast.success('Revisión actualizada. Notificaciones se enviarán al terminar el renderizado.');
            } else {
              toast.warning('Revisión publicada pero PENDIENTE de renderizado. Las notificaciones NO se enviaron: reintentá «Renderizar» y se enviarán al terminar.', { duration: 12000 });
            }
          } else {
            toast.success('Revisión actualizada correctamente con nuevo PDF');
          }
          
        } else {
          // Cambio normal sin revisión (solo metadata)
          await documentosAPI.updateManual(selectedDoc.id, data);
          toast.success('Manual actualizado correctamente');
        }
      } else {
        // === CREACIÓN DE DOCUMENTO NUEVO ===
        const createRes = await documentosAPI.createManual(data);
        const newDocId = createRes.data?.id;
        
        if (revisionPdfFile && newDocId) {
          // Paso 0: decidir el precacheo AHORA para saber si diferir notificaciones a usuarios
          let willPrecache = false;
          let forceLarge = false;
          if (precachearAlSubir) {
            const decision = await askPrecacheDecision(revisionPdfFile.size);
            willPrecache = decision.willPrecache;
            forceLarge = decision.forceLarge;
          }
          
          toast.info('Subiendo PDF del nuevo documento...');
          try {
            const file_key = await uploadPdfToR2(newDocId, revisionPdfFile);
            
            // confirmR2Upload envía notifs para docs nuevos. Diferimos si vamos a precachear.
            const confirmFormData = new FormData();
            confirmFormData.append('file_key', file_key);
            await documentosAPI.confirmR2Upload(newDocId, confirmFormData, willPrecache);
            
            if (willPrecache) {
              toast.success('Documento creado. Notificaciones se enviarán al terminar el renderizado.');
              const enqueued = await enqueuePrecacheIndividual(
                newDocId,
                manualForm.numero_referencia || manualForm.nombre || 'documento',
                forceLarge
              );
              if (!enqueued) {
                // Aviso al admin: el precache no se encoló y la notif quedó pendiente.
                toast.warning('Precacheo no encolado. La notificación quedó PENDIENTE. Reintentá el precacheo manualmente desde el panel para que se dispare.');
              }
            } else {
              toast.success('Documento creado con PDF correctamente');
            }
          } catch (uploadErr) {
            console.error('Error subiendo PDF del nuevo doc:', uploadErr);
            toast.error('Documento creado pero el PDF no se pudo subir. Puede subirlo manualmente.');
          }
        } else {
          toast.success('Documento creado correctamente');
        }
      }
      setShowForm(false);
      resetForm();
      setSelectedDoc(null);
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al guardar documento'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUpload = (doc) => {
    setSelectedDoc(doc);
    // Si ya tiene PDF previo, es una RESUBIDA → pedir confirmación para evitar errores.
    // El backend NO dispara notificaciones en este flujo (es_documento_nuevo = false),
    // así que la sustitución es silenciosa. Solo se reemplaza el archivo.
    if (doc.pdf_url) {
      setShowResubirConfirm(true);
    } else {
      // Primera carga del PDF → va directo al diálogo de upload
      setShowUploadDialog(true);
    }
  };

  const handleDeleteDocument = async (doc) => {
    const confirmar = window.confirm(
      `¿Está seguro de eliminar el documento "${doc.nombre}"?\n\nEsta acción eliminará el documento y su PDF asociado permanentemente.`
    );
    if (!confirmar) return;
    
    try {
      await documentosAPI.deleteManual(doc.id);
      toast.success('Documento eliminado correctamente');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al eliminar documento'));
    }
  };

  const resetForm = () => {
    setManualForm({
      fabricante_id: '',
      modelos_aplicables: [],
      tipo_manual: 'manual_mantenimiento',
      tipo_manual_custom: '',
      numero_referencia: '',
      nombre: '',
      revision: '',
      fecha_revision: '',
      precio_usd: 0,
      es_documento_general: false,
      anular_notificaciones: false
    });
    setSelectedFab('');
    setModelos([]);
    setModeloSelectorFab('');
    setModeloSelectorModelos([]);
    setModelosSeleccionados([]);
    setRevisionOriginal('');
    setFechaRevisionOriginal('');
    setRevisionCambiada(false);
    setRevisionPdfFile(null);
  };

  const clearFilters = () => {
    setFilterFabricante('');
    setFilterModelo('');
    setSearchTerm('');
  };

  const getFabricanteName = (fabId) => {
    return fabricantes.find(f => f.id === fabId)?.nombre || 'Desconocido';
  };

  const getTipoLabel = (tipoValue) => {
    const tipo = tiposDocumento.find(t => t.value === tipoValue);
    return tipo ? tipo.label : tipoValue;
  };

  // Filtrar documentos por búsqueda, fabricante y modelo
  const filterDocumentos = (docs) => {
    return docs.filter(doc => {
      const matchSearch = !searchTerm || 
        doc.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.numero_referencia?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtrado de fabricante - mostrar docs que:
      // 1. Fueron CREADOS por el fabricante (fabricante_id)
      // 2. O que AFECTAN a modelos de ese fabricante (modelos_aplicables contiene modelos del fabricante)
      let matchFabricante = true;
      if (filterFabricante) {
        const esCreador = doc.fabricante_id === filterFabricante;
        
        // Verificar si algún modelo pertenece al fabricante filtrado
        const afectaModelosDelFabricante = (doc.modelos_aplicables || []).some(modeloRef => {
          // Formato nuevo: "fabricante_id_modelo_nombre"
          if (modeloRef.startsWith(filterFabricante + '_')) {
            return true;
          }
          return false;
        });
        
        matchFabricante = esCreador || afectaModelosDelFabricante;
      }
      
      // Filtrado de modelo - manejar ambos formatos en modelos_aplicables
      let matchModelo = true;
      if (filterModelo && doc.modelos_aplicables) {
        matchModelo = doc.modelos_aplicables.some(modeloRef => {
          // Coincidencia directa (formato antiguo)
          if (modeloRef === filterModelo) return true;
          
          // Coincidencia en formato nuevo: verificar si termina con _filterModelo
          if (filterFabricante && modeloRef.startsWith(filterFabricante + '_')) {
            const modeloNombre = modeloRef.substring(filterFabricante.length + 1);
            return modeloNombre === filterModelo;
          }
          
          // Sin filtro de fabricante, solo verificar el nombre
          const parts = modeloRef.split('_');
          if (parts.length > 1) {
            const modeloNombre = parts.slice(1).join('_');
            return modeloNombre === filterModelo;
          }
          
          return false;
        });
      }
      
      return matchSearch && matchFabricante && matchModelo;
    });
  };

  // Filtrar por categoría
  const getManuales = () => filterDocumentos(manuales.filter(m => tiposManuales.includes(m.tipo_manual)));
  const getPublicacionesServicio = () => filterDocumentos(manuales.filter(m => tiposPublicacionesServicio.includes(m.tipo_manual)));
  const getOtrasPublicaciones = () => filterDocumentos(manuales.filter(m => 
    tiposOtrasPublicaciones.includes(m.tipo_manual) || 
    (!tiposManuales.includes(m.tipo_manual) && !tiposPublicacionesServicio.includes(m.tipo_manual))
  ));

  // Componente de tarjeta de documento reutilizable
  // eslint-disable-next-line react/no-unstable-nested-components
  const DocumentCard = ({ doc, colorClass = 'bg-blue-100', iconColorClass = 'text-blue-600' }) => (
    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${doc.pdf_url ? colorClass : 'bg-gray-100'}`}>
          <FileText className={`h-6 w-6 ${doc.pdf_url ? iconColorClass : 'text-gray-400'}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{doc.nombre}</p>
            {doc.es_documento_general && (
              <Badge className="bg-amber-500 text-white text-xs">
                General
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="text-xs">{getTipoLabel(doc.tipo_manual)}</Badge>
            <span className="text-sm text-gray-500">Ref: {doc.numero_referencia}</span>
            {doc.revision && <span className="text-sm text-gray-500">Rev: {doc.revision}</span>}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {getFabricanteName(doc.fabricante_id)} • {doc.es_documento_general ? 'Todos los suscriptores' : `${doc.modelos_aplicables?.length || 0} modelos`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="text-lg font-bold text-green-600">${doc.precio_usd || 0}</p>
          <p className="text-xs text-gray-500">USD</p>
        </div>
        <div className="flex gap-2">
          {doc.pdf_url && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleViewPdf(doc)}>
                <Eye className="h-4 w-4 mr-1" /> Ver
              </Button>
              {/* Botón de renderizar (LOW+HD+TOC+OCR) con badge de progreso.
                  Si el PDF actual ya fue precacheado, el botón queda "Renderizado ✓" (gris, deshabilitado).
                  Al subir una revisión nueva, r2_file_key cambia → precache_done_r2_key deja de matchear → botón vuelve a estar habilitado. */}
              <PrecacheButtonWithBadge
                manualId={doc.id}
                docName={doc.numero_referencia || doc.nombre || 'documento'}
                fileSizeBytes={doc.pdf_size || 0}
                variant="card"
                alreadyRendered={
                  !!doc.r2_file_key &&
                  !!doc.precache_done_r2_key &&
                  doc.r2_file_key === doc.precache_done_r2_key
                }
              />
              {/* Indicador de estado de procesamiento */}
              {doc.paginas_procesadas && (
                <Badge variant="outline" className="text-green-600 border-green-300">
                  <CheckCircle className="h-3 w-3 mr-1" /> Procesado
                </Badge>
              )}
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => handleOpenUpload(doc)}>
            <Upload className="h-4 w-4 mr-1" /> {doc.pdf_url ? 'Resubir' : 'Subir'} PDF
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleEditManual(doc)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleDeleteDocument(doc)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestión de Documentos</h1>
          <Button onClick={() => { resetForm(); setShowForm(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Nuevo Documento
          </Button>
        </div>

        {/* Filtros globales - INLINE para evitar re-renders */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-4 gap-4">
              {/* Buscador */}
              <div className="relative sm:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o referencia..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filtro Fabricante */}
              <Select value={filterFabricante} onValueChange={(v) => { setFilterFabricante(v === 'all' ? '' : v); setFilterModelo(''); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por fabricante" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los fabricantes</SelectItem>
                  {fabricantes.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Filtro Modelo */}
              <Select 
                value={filterModelo} 
                onValueChange={(v) => setFilterModelo(v === 'all' ? '' : v)}
                disabled={!filterFabricante}
              >
                <SelectTrigger className={!filterFabricante ? 'opacity-50' : ''}>
                  <SelectValue placeholder={filterFabricante ? "Filtrar por modelo" : "Selecciona fabricante"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los modelos</SelectItem>
                  {filterModelos.map(m => (
                    <SelectItem key={m.id || m} value={m.nombre || m}>{m.nombre || m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(searchTerm || filterFabricante || filterModelo) && (
              <div className="flex items-center gap-2 mt-4">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Filtros activos:</span>
                {searchTerm && <Badge variant="secondary">Búsqueda: &quot;{searchTerm}&quot;</Badge>}
                {filterFabricante && <Badge variant="secondary">{getFabricanteName(filterFabricante)}</Badge>}
                {filterModelo && <Badge variant="secondary">{filterModelo}</Badge>}
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500 hover:text-red-700">
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="manuales">
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="manuales" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2">
              <BookOpen className="h-4 w-4 shrink-0" /> <span>Manuales ({getManuales().length})</span>
            </TabsTrigger>
            <TabsTrigger value="publicaciones_servicio" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2">
              <Newspaper className="h-4 w-4 shrink-0" /> <span className="truncate">Pub. Servicio ({getPublicacionesServicio().length})</span>
            </TabsTrigger>
            <TabsTrigger value="otras" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2">
              <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">Otras ({getOtrasPublicaciones().length})</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB: MANUALES */}
          <TabsContent value="manuales">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Manuales Técnicos</CardTitle>
                <p className="text-sm text-gray-500">Manual de Mantenimiento, Service Manual, Catálogo de Partes, Manual de Prácticas Estándar, Overhaul Manual</p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-600" />
                  </div>
                ) : getManuales().length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay manuales que coincidan con los filtros</p>
                ) : (
                  <div className="grid gap-4">
                    {getManuales().map(doc => (
                      <DocumentCard key={doc.id} doc={doc} colorClass="bg-blue-100" iconColorClass="text-blue-600" />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: PUBLICACIONES DE SERVICIO */}
          <TabsContent value="publicaciones_servicio">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Publicaciones de Servicio</CardTitle>
                <p className="text-sm text-gray-500">Directivas de Aeronavegabilidad, Service Bulletins</p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-600" />
                  </div>
                ) : getPublicacionesServicio().length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay publicaciones de servicio que coincidan con los filtros</p>
                ) : (
                  <div className="grid gap-4">
                    {getPublicacionesServicio().map(doc => (
                      <DocumentCard key={doc.id} doc={doc} colorClass="bg-orange-100" iconColorClass="text-orange-600" />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: OTRAS PUBLICACIONES */}
          <TabsContent value="otras">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Otras Publicaciones</CardTitle>
                <p className="text-sm text-gray-500">Service Letters, Service Instructions, Service Advisory, Circular Técnica</p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-600" />
                  </div>
                ) : getOtrasPublicaciones().length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay otras publicaciones que coincidan con los filtros</p>
                ) : (
                  <div className="grid gap-4">
                    {getOtrasPublicaciones().map(doc => (
                      <DocumentCard key={doc.id} doc={doc} colorClass="bg-green-100" iconColorClass="text-green-600" />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Nuevo/Editar Documento */}
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { resetForm(); setSelectedDoc(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedDoc?.id ? 'Editar Documento' : 'Nuevo Documento'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fabricante *</Label>
                  <Select 
                    value={manualForm.fabricante_id} 
                    onValueChange={(v) => {
                      setManualForm({ ...manualForm, fabricante_id: v, modelos_aplicables: [] });
                      setSelectedFab(v);
                    }}
                    disabled={!!selectedDoc?.id}
                  >
                    <SelectTrigger className={selectedDoc?.id ? 'bg-gray-100 cursor-not-allowed' : ''}>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fabricantes.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDoc?.id && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ El fabricante no se puede modificar</p>
                  )}
                </div>
                <div>
                  <Label>Tipo de Documento *</Label>
                  <Select 
                    value={manualForm.tipo_manual} 
                    onValueChange={(v) => setManualForm({ ...manualForm, tipo_manual: v, tipo_manual_custom: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Publicaciones de Servicio */}
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-orange-50">Publicaciones de Servicio</div>
                      {tiposDocumento.filter(t => t.categoria === 'Publicaciones de Servicio').map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                      {/* Manuales */}
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-blue-50 mt-1">Manuales</div>
                      {tiposDocumento.filter(t => t.categoria === 'Manuales').map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                      {/* Otras Publicaciones */}
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-green-50 mt-1">Otras Publicaciones</div>
                      {tiposDocumento.filter(t => t.categoria === 'Otras Publicaciones').map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {manualForm.tipo_manual === 'otro' && (
                    <Input
                      className="mt-2"
                      value={manualForm.tipo_manual_custom}
                      onChange={(e) => setManualForm({ ...manualForm, tipo_manual_custom: e.target.value })}
                      placeholder="Escribir nombre del tipo"
                    />
                  )}
                </div>
              </div>

              {/* Selector de Modelos Aplicables con Select All */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-medium">Modelos Aplicables</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Selecciona los modelos a los que aplica este documento. Puedes agregar modelos de diferentes fabricantes.
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-sm">Fabricante</Label>
                    <Select 
                      value={modeloSelectorFab} 
                      onValueChange={(v) => {
                        setModeloSelectorFab(v);
                        loadModelosSelector(v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar fabricante..." />
                      </SelectTrigger>
                      <SelectContent>
                        {fabricantes.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Modelo</Label>
                    <Select 
                      value=""
                      onValueChange={(modeloId) => {
                        const modelo = modeloSelectorModelos.find(m => m.id === modeloId);
                        if (modelo) agregarModelo(modelo);
                      }}
                      disabled={!modeloSelectorFab || modeloSelectorModelos.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={modeloSelectorModelos.length === 0 ? "Sin modelos" : "Agregar modelo..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {modeloSelectorModelos.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Select All checkbox */}
                {modeloSelectorFab && modeloSelectorModelos.length > 0 && (
                  <div className="flex items-center space-x-2 mb-3 p-2 bg-blue-50 rounded">
                    <Checkbox 
                      id="selectAll"
                      onCheckedChange={seleccionarTodosModelos}
                    />
                    <label htmlFor="selectAll" className="text-sm font-medium cursor-pointer">
                      Seleccionar todos los modelos de {fabricantes.find(f => f.id === modeloSelectorFab)?.nombre} ({modeloSelectorModelos.length})
                    </label>
                  </div>
                )}

                {/* Lista de modelos seleccionados */}
                {modelosSeleccionados.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {modelosSeleccionados.map(m => (
                      <Badge 
                        key={m.id} 
                        variant="secondary" 
                        className="flex items-center gap-1 px-2 py-1"
                      >
                        <span className="text-xs text-gray-500">{m.fabricante_nombre}:</span>
                        <span>{m.nombre}</span>
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500" 
                          onClick={() => quitarModelo(m.id)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-400 mt-2">
                  {modelosSeleccionados.length} modelo(s) seleccionado(s)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Número de Referencia *</Label>
                  <Input
                    value={manualForm.numero_referencia}
                    onChange={(e) => setManualForm({ ...manualForm, numero_referencia: e.target.value })}
                    placeholder="Ej: D3011-13-FR"
                    disabled={!!selectedDoc?.id}
                    className={selectedDoc?.id ? 'bg-gray-100 cursor-not-allowed' : ''}
                  />
                  {selectedDoc?.id && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ El número de referencia no se puede modificar</p>
                  )}
                </div>
                <div>
                  <Label>Revisión</Label>
                  <Input
                    value={manualForm.revision}
                    onChange={(e) => setManualForm({ ...manualForm, revision: e.target.value })}
                    placeholder="Ej: R12, 2025-01"
                    className={selectedDoc?.id && manualForm.revision !== revisionOriginal && revisionOriginal ? 'border-red-500 border-2' : ''}
                  />
                  {selectedDoc?.id && manualForm.revision !== revisionOriginal && revisionOriginal && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      Al cambiar la revisión, debe actualizar la fecha y seleccionar el nuevo PDF
                    </p>
                  )}
                </div>
              </div>

              {/* Selector de PDF: visible cuando cambia revisión o es documento nuevo */}
              {((selectedDoc?.id && manualForm.revision !== revisionOriginal && revisionOriginal) || !selectedDoc?.id) && (
                <div className="p-3 rounded-lg border-2 border-dashed border-teal-300 bg-teal-50">
                  <Label className="text-sm font-medium text-teal-800 mb-2 block">
                    {selectedDoc?.id ? 'Nuevo PDF (obligatorio para cambio de revisión) *' : 'Archivo PDF *'}
                  </Label>
                  <input
                    type="file"
                    accept=".pdf"
                    data-testid="revision-pdf-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        if (!f.name.toLowerCase().endsWith('.pdf')) {
                          toast.error('Solo se permiten archivos PDF');
                          return;
                        }
                        if (f.size > 800 * 1024 * 1024) {
                          toast.error('El archivo es muy grande. Máximo: 800 MB');
                          return;
                        }
                        setRevisionPdfFile(f);
                      }
                    }}
                    className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-600 file:text-white file:cursor-pointer hover:file:bg-teal-700"
                  />
                  {revisionPdfFile && (
                    <p className="text-xs text-teal-700 mt-1">
                      Seleccionado: {revisionPdfFile.name} ({(revisionPdfFile.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  )}

                  {/* Checkbox: precachear individualmente al terminar la subida */}
                  {revisionPdfFile && (
                    <label
                      className="mt-3 flex items-start gap-2 cursor-pointer select-none"
                      data-testid="precache-al-subir-label"
                    >
                      <input
                        type="checkbox"
                        checked={precachearAlSubir}
                        onChange={(e) => setPrecachearAlSubir(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-teal-700"
                        data-testid="precache-al-subir-checkbox"
                      />
                      <span className="text-xs text-teal-800 leading-tight">
                        <strong>Renderizar y procesar en HD ahora</strong>
                        <br />
                        <span className="text-teal-700/80">
                          Ejecuta LOW + HD + TOC + búsqueda de texto <strong>antes de avisar a los usuarios</strong>.
                          Recomendado en horario de baja demanda o para documentos críticos.
                          <br />
                          Si lo dejás <strong>destildado</strong>, el documento se publica y notifica de inmediato — el renderizado se puede disparar después con el botón <strong>🔄</strong> en el card o desde el visor.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}

              <div>
                <Label>Nombre del Documento *</Label>
                <Input
                  value={manualForm.nombre}
                  onChange={(e) => setManualForm({ ...manualForm, nombre: e.target.value })}
                  placeholder="Ej: Pilot's Operating Handbook"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha de Revisión</Label>
                  <Input
                    type="date"
                    value={manualForm.fecha_revision}
                    onChange={(e) => setManualForm({ ...manualForm, fecha_revision: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    Precio USD *
                  </Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={manualForm.precio_usd_display !== undefined ? manualForm.precio_usd_display : manualForm.precio_usd}
                    onChange={(e) => {
                      let val = e.target.value;
                      // Permitir solo números, punto y coma
                      val = val.replace(/[^0-9.,]/g, '');
                      // Reemplazar coma por punto para el parsing
                      const numericVal = val.replace(',', '.');
                      const parsed = parseFloat(numericVal);
                      setManualForm({ 
                        ...manualForm, 
                        precio_usd_display: val,
                        precio_usd: isNaN(parsed) ? 0 : parsed 
                      });
                    }}
                    onBlur={(e) => {
                      // Al salir del campo, formatear correctamente
                      const val = manualForm.precio_usd || 0;
                      setManualForm({
                        ...manualForm,
                        precio_usd_display: val.toString(),
                        precio_usd: val
                      });
                    }}
                    placeholder="0.00"
                    className="text-lg font-semibold"
                  />
                </div>
              </div>

              {/* Checkbox: Documento General */}
              <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="es_documento_general"
                    checked={manualForm.es_documento_general}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, es_documento_general: checked })}
                  />
                  <div className="flex-1">
                    <label htmlFor="es_documento_general" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Documento General (aplica a todos)
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      Marcar si es un documento de autoridad (FAA, ANAC, etc.) que aplica a <strong>todos los suscriptores activos</strong>. 
                      Se enviará notificación y se dará acceso automático a todos los usuarios con suscripción activa.
                    </p>
                    {manualForm.es_documento_general && (
                      <p className="text-xs text-amber-700 mt-2 font-medium">
                        ⚠️ Los modelos seleccionados arriba serán ignorados. Este documento será visible para TODOS los suscriptores activos.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Checkbox: Anular Notificaciones (disponible tanto al crear como al actualizar) */}
              <div className="border rounded-lg p-4 bg-slate-50 border-slate-200">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="anular_notificaciones"
                    checked={manualForm.anular_notificaciones}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, anular_notificaciones: checked })}
                    data-testid="anular-notificaciones-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="anular_notificaciones" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <BellOff className="h-4 w-4 text-slate-600" />
                      Anular notificaciones
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedDoc?.id ? (
                        <>La actualización se guarda normalmente pero <strong>sin enviar notificaciones</strong> (ni email, ni campanita, ni push). Útil para correcciones menores o cargas silenciosas.</>
                      ) : (
                        <>El documento se incorpora normalmente pero <strong>sin enviar notificaciones</strong> (ni email, ni campanita, ni push). Útil para cargar documentación antigua en lote.</>
                      )}
                    </p>
                    {manualForm.anular_notificaciones && (
                      <p className="text-xs text-slate-700 mt-2 font-medium">
                        🔕 {selectedDoc?.id ? 'Esta actualización será silenciosa.' : 'Este documento se incorporará silenciosamente.'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); setSelectedDoc(null); }} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleSaveManual} disabled={saving} className="flex-1 bg-teal-500 hover:bg-teal-600">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : (selectedDoc?.id ? <Edit className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />)}
                  {selectedDoc?.id ? 'Guardar Cambios' : 'Crear Documento'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Upload PDF */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Subir PDF - {selectedDoc?.nombre}</DialogTitle>
            </DialogHeader>
            <R2FileUpload
              documentId={selectedDoc?.id}
              maxSize={800}
              onSuccess={() => {
                setShowUploadDialog(false);
                loadData();
                toast.success('PDF subido correctamente');
              }}
              onError={(error) => {
                toast.error(error);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Confirmación de Resubida silenciosa de PDF */}
        <AlertDialog open={showResubirConfirm} onOpenChange={setShowResubirConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <BellOff className="h-5 w-5 text-orange-500" />
                ¿Cambiar el PDF sin modificar la revisión?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <p>
                    Vas a reemplazar el PDF de <strong>{selectedDoc?.nombre}</strong> sin cambiar el número de revisión ni la fecha de revisión.
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-orange-900">
                    <strong>Sustitución silenciosa</strong> — no se enviarán notificaciones (mail / campana / push). Ideal para corregir un PDF mal cargado, corrupto o cuando el secretario subió el archivo equivocado.
                  </div>
                  <p className="text-gray-600">
                    Si lo que querés es <strong>publicar una nueva revisión</strong> (cambia nro. de revisión + fecha + PDF + notifica a suscriptores), elegí <strong>"Editar revisión"</strong> abajo.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <AlertDialogCancel data-testid="resubir-cancelar-btn">Cancelar</AlertDialogCancel>
              <Button
                variant="outline"
                onClick={() => {
                  setShowResubirConfirm(false);
                  if (selectedDoc) handleEditManual(selectedDoc);
                }}
                data-testid="resubir-editar-revision-btn"
              >
                <Edit className="h-4 w-4 mr-1" /> Editar revisión
              </Button>
              <AlertDialogAction
                onClick={() => {
                  setShowResubirConfirm(false);
                  setShowUploadDialog(true);
                }}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="resubir-confirmar-btn"
              >
                <Upload className="h-4 w-4 mr-1" /> Sí, solo cambiar PDF
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmación de precacheo para PDFs > 150MB */}
        <AlertDialog
          open={showLargePrecacheConfirm}
          onOpenChange={(open) => {
            // Si el usuario cierra sin decidir, resolvemos con false (skip)
            if (!open && largePrecacheContext?.resolveFn) {
              largePrecacheContext.resolveFn(false);
              setLargePrecacheContext(null);
            }
            setShowLargePrecacheConfirm(open);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Este documento es grande ({largePrecacheContext?.sizeMB || 0}MB)
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>
                    El documento <strong>{largePrecacheContext?.docName}</strong> supera los 150MB.
                    Precachearlo ahora puede tardar bastante y consumir recursos del servidor.
                  </p>
                  <p>¿Estás seguro que deseas renderizar este documento en este momento?</p>
                  <p className="text-gray-600 text-xs">
                    Si elegís <strong>NO</strong>, podés precachearlo más tarde desde el panel de {'"Estado de Documentación"'} con el botón de PDFs grandes (&gt;150MB).
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <AlertDialogCancel
                data-testid="precache-large-no-btn"
                onClick={() => {
                  if (largePrecacheContext?.resolveFn) {
                    largePrecacheContext.resolveFn(false);
                  }
                  setLargePrecacheContext(null);
                }}
              >
                No, hacerlo después
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="precache-large-si-btn"
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => {
                  if (largePrecacheContext?.resolveFn) {
                    largePrecacheContext.resolveFn(true);
                  }
                  setLargePrecacheContext(null);
                }}
              >
                Sí, precachear ahora
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PDF Viewer Pro - Visor premium para todos */}
        <PDFViewerPro
          isOpen={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          documentId={selectedDoc?.id}
          documentName={selectedDoc?.nombre}
          numeroReferencia={selectedDoc?.numero_referencia}
          canPrint={true}
          maxPrintPages={9999}
          isAdmin={true}
          isSuperAdmin={true}
        />
      </div>
    </div>
  );
};

export default function AdminDocumentosV2WithProviders() {
  return (
    <IndividualPrecacheStatusProvider enabled={true}>
      <AdminDocumentosV2 />
    </IndividualPrecacheStatusProvider>
  );
}
